import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader, LoadingState } from '../components/ui';
import { Icon } from '../components/icons';
import { SALES_STEPS, SALES_SIDE_TOOLS } from '../vertrieb/salesSteps';
import { KpiRow, Kpi } from '../vertrieb/KpiRow';
import { useSalesOverview, loadSalesOverview } from '../vertrieb/useSalesOverview';
import { euro, faelligkeit, datum } from '../vertrieb/format';
import { useBerechtigung } from '../berechtigungen/useBerechtigung';
import { useAuth } from '../contexts/AuthContext';
import { findRoute, darfSehen } from '../navigation/routes';

/**
 * Vertriebs-Cockpit: was heute dran ist, wie die Pipeline steht und wo im
 * Ablauf die einzelnen Seiten liegen. Frueher gab es nur die Seiten selbst,
 * ohne Ort, der sie zusammenhaelt.
 *
 * Welle 2: Angebote und Postfaecher laedt das Cockpit nur mit angebot.lesen
 * bzw. versand.lesen; Links und Knoepfe erscheinen nur, wenn die Zielseite
 * bzw. die Aktion fuer die Person erlaubt ist.
 */
const VertriebPage: React.FC = () => {
  const { darf } = useBerechtigung();
  const { user } = useAuth();
  const siehtAngebote = darf('angebot.lesen');
  const siehtPostfaecher = darf('versand.lesen');
  const loader = useCallback(
    () => loadSalesOverview({ angebote: siehtAngebote, postfaecher: siehtPostfaecher }),
    [siehtAngebote, siehtPostfaecher],
  );
  const { data, loading, loaded, refresh } = useSalesOverview(loader);
  const darfOeffnen = (pfad: string) => {
    const eintrag = findRoute(pfad.split('?')[0]);
    return !eintrag || darfSehen(user, eintrag);
  };
  const { aufgaben, antworten, pipeline, faellig, angebote, postfaecher, fehler } = data;

  const dranZaehler = (aufgaben?.ueberfaellig ?? 0) + (aufgaben?.heute ?? 0) + (antworten?.offeneAntworten ?? 0) + faellig.length;

  const kpis: Kpi[] = [
    { label: 'überfällige Aufgaben', value: aufgaben?.ueberfaellig ?? '–', tone: aufgaben?.ueberfaellig ? 'warn' : 'neutral', to: '/aufgaben' },
    { label: 'offene Antworten', value: antworten?.offeneAntworten ?? '–', tone: antworten?.offeneAntworten ? 'warn' : 'neutral', to: '/outreach-inbox' },
    { label: 'offene Vorgänge', value: pipeline?.offeneVorgaenge ?? '–', to: '/pipeline' },
    { label: 'gewichtete Prognose', value: pipeline ? euro(pipeline.gewichteterWert) : '–', to: '/pipeline', hint: pipeline ? `${euro(pipeline.offenerWert)} offen` : undefined },
    ...(siehtAngebote ? [{ label: 'Angebote verschickt', value: angebote?.verschickt ?? '–', to: '/angebote?status=sent', hint: angebote ? euro(angebote.offenerWert / 100) : undefined }] : []),
    { label: 'gewonnen', value: pipeline?.gewonnen ?? '–', tone: 'good', to: '/pipeline', hint: pipeline ? euro(pipeline.gewonnenerWert) : undefined },
    ...(siehtPostfaecher ? [{ label: 'Postfächer mit Problem', value: postfaecher?.kritisch ?? '–', tone: postfaecher?.kritisch ? 'bad' as const : 'neutral' as const, to: '/mailboxes' }] : []),
  ];

  return (
    <div className="page-container-wide">
      <PageHeader
        eyebrow={<><Icon name="target" size={14} /> Vertrieb</>}
        title="Cockpit"
        subtitle="Was heute dran ist, wie die Pipeline steht und wo im Ablauf du gerade bist."
        actions={(
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => refresh()} disabled={loading}>
            <Icon name="refresh" size={14} /> {loading ? 'Aktualisiere…' : 'Aktualisieren'}
          </button>
        )}
      />

      {fehler.length > 0 && (
        <div className="alert alert-warning">
          Teile des Cockpits konnten nicht geladen werden ({fehler.join('; ')}).
        </div>
      )}

      {!loaded && loading ? <LoadingState rows={3} label="Cockpit wird geladen" /> : (
        <KpiRow items={kpis} aria-label="Vertriebskennzahlen" />
      )}

      <div className="sales-grid">
        <section className="card sales-card" aria-label="Heute dran">
          <div className="sales-card__head">
            <span className="sales-card__icon"><Icon name="sun" size={18} /></span>
            <h2 className="card-title">Heute dran</h2>
            <span className="badge badge-neutral">{dranZaehler}</span>
            <Link to="/aufgaben" className="sales-card__more">Heute <Icon name="arrow-right" size={14} /></Link>
          </div>

          {loaded && dranZaehler === 0 && (
            <p className="text-sm text-secondary">Nichts Dringendes. Zeit für Outreach oder die nächste Kampagne.</p>
          )}

          {aufgaben && aufgaben.liste.length > 0 && (
            <ul className="sales-list">
              {aufgaben.liste.map((t) => {
                const f = faelligkeit(t.dueAt);
                return (
                  <li key={t.id}>
                    <Icon name={t.kind === 'call' ? 'megaphone' : t.kind === 'email' ? 'mail' : t.kind === 'meeting' ? 'calendar' : 'check'} size={14} />
                    <span className="sales-list__text">
                      <Link to={`/aufgaben?aufgabe=${encodeURIComponent(t.id)}`} title="Aufgabe öffnen"><strong>{t.title}</strong></Link>
                      {t.company && <> · <Link to={`/companies/${t.company.id}`}>{t.company.name}</Link></>}
                      {t.deal && <> · <Link to={`/pipeline?deal=${t.deal.id}`}>{t.deal.title}</Link></>}
                    </span>
                    {f && <span className={`sales-list__due${f.ueberfaellig ? ' is-overdue' : ''}`}>{f.text}</span>}
                  </li>
                );
              })}
            </ul>
          )}

          {antworten && antworten.offeneAntworten > 0 && (
            <Link to="/outreach-inbox" className="sales-hint">
              <Icon name="inbox" size={14} /> {antworten.offeneAntworten} offene {antworten.offeneAntworten === 1 ? 'Antwort' : 'Antworten'} warten auf Bearbeitung
            </Link>
          )}

          {faellig.length > 0 && (
            <>
              <h3 className="sales-card__sub">Fällige Vorgänge</h3>
              <ul className="sales-list">
                {faellig.map((d) => {
                  const f = faelligkeit(d.nextStepAt);
                  return (
                    <li key={d.id}>
                      <Icon name="kanban" size={14} />
                      <span className="sales-list__text">
                        <Link to={`/pipeline?deal=${d.id}`}><strong>{d.title}</strong></Link>
                        {d.company && <> · {d.companyId ? <Link to={`/companies/${d.companyId}`}>{d.company}</Link> : d.company}</>}
                        {d.nextStep && <> · {d.nextStep}</>}
                      </span>
                      {f && <span className={`sales-list__due${f.ueberfaellig ? ' is-overdue' : ''}`}>{f.text}</span>}
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </section>

        <section className="card sales-card" aria-label="Pipeline auf einen Blick">
          <div className="sales-card__head">
            <span className="sales-card__icon"><Icon name="kanban" size={18} /></span>
            <h2 className="card-title">Pipeline</h2>
            <Link to="/pipeline" className="sales-card__more">Öffnen <Icon name="arrow-right" size={14} /></Link>
          </div>
          {pipeline ? (
            <dl className="sales-facts">
              <div><dt>offen</dt><dd>{pipeline.offeneVorgaenge} Vorgänge · {euro(pipeline.offenerWert)}</dd></div>
              <div><dt>gewichtet</dt><dd>{euro(pipeline.gewichteterWert)}</dd></div>
              <div><dt>gewonnen</dt><dd>{pipeline.gewonnen} · {euro(pipeline.gewonnenerWert)}</dd></div>
              <div><dt>verloren</dt><dd>{pipeline.verloren}</dd></div>
            </dl>
          ) : (
            <p className="text-sm text-secondary">{loaded ? 'Keine Pipeline-Daten.' : 'Wird geladen…'}</p>
          )}
          {angebote && (
            <dl className="sales-facts">
              <div><dt>Angebote</dt><dd>{angebote.entwurf} Entwürfe · {angebote.verschickt} verschickt · {angebote.angenommen} angenommen{angebote.abgelaufen ? ` · ${angebote.abgelaufen} abgelaufen` : ''}</dd></div>
            </dl>
          )}
          {(darf('vertrieb.bearbeiten') || darf('angebot.bearbeiten')) && (
            <div className="btn-group">
              {darf('vertrieb.bearbeiten') && <Link to="/pipeline" className="btn btn-primary btn-sm">Vorgang anlegen</Link>}
              {darf('angebot.bearbeiten') && <Link to="/angebote" className="btn btn-secondary btn-sm">Angebot schreiben</Link>}
            </div>
          )}
        </section>
      </div>

      <h2 className="flow-section-title">Der Ablauf</h2>
      <ol className="flow-grid" aria-label="Vertriebsschritte">
        {SALES_STEPS.filter((s) => darfOeffnen(s.path)).map((s) => (
          <li key={s.id} className="flow-card" data-state="ready">
            <div className="flow-card__head">
              <span className="flow-card__index" aria-hidden="true">{s.index}</span>
              <span className="flow-card__icon" aria-hidden="true"><Icon name={s.icon} size={18} /></span>
            </div>
            <h3 className="flow-card__title"><Link to={s.path}>{s.index} · {s.label}</Link></h3>
            <p className="flow-card__text">{s.description}</p>
            <div className="flow-card__actions">
              <Link to={s.path} className="btn btn-secondary btn-sm">Öffnen</Link>
            </div>
          </li>
        ))}
      </ol>

      <h2 className="flow-section-title">Werkzeuge</h2>
      <ul className="flow-side-grid" aria-label="Werkzeuge für den Vertrieb">
        {SALES_SIDE_TOOLS.filter((t) => darfOeffnen(t.path)).map((t) => (
          <li key={t.path} className="flow-side-card">
            <span className="flow-card__icon" aria-hidden="true"><Icon name={t.icon} size={18} /></span>
            <div>
              <Link to={t.path} className="flow-side-card__title">{t.label}</Link>
              <p className="flow-card__text">{t.description}</p>
              {t.path === '/mailboxes' && postfaecher && (
                <p className="flow-card__meta">
                  {postfaecher.postfaecher} Postfächer · {postfaecher.warmupAktiv} im Warm-up · zuletzt geprüft {datum(postfaecher.zuletztGeprueft)}
                </p>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default VertriebPage;
